import { NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatColumnDef, MatCellDef, MatCell, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { CartActions, CartLineSources, CartSelectors } from '@next/core-lib/cart';
import { AgrNotificationEmptyModule } from '@next/core-lib/components';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { RemoveSpecialCharactersPipe } from '@next/core-lib/pipes';

import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableV2Module } from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { CatalogItemVM } from 'src/app/models/catalog-item.model';
import { SortMode } from 'src/app/models/sort-mode.model';
import { TableDisplayMode } from 'src/app/models/table-display-mode';
import { ElasticApiService } from 'src/app/services/elastic-api.service';
import { ShopFeatureFlags } from 'src/app/shared/shop-feature-flags';
import { setCatalogProductSearchString, setCatalogSortMode } from 'src/app/store/actions';
import { selectCatalogProductSearchString, selectcatalogTotalNumberOfProductsFound, selectSelectedCatalogCategory } from 'src/app/store/selectors';
import { validateStep } from 'src/app/validators/step.validator';

import { ProductsDataSource } from './products.data-source';
import { FormatAddProductToCartButtonTextPipe } from '../../product/pipes/format-add-product-to-cart-button-text.pipe';
import * as ProductActions from '../../product/store/actions';

import { FavoritesStarComponent } from '../../standalone/favorites-star/favorites-star.component';
import { CatalogProductDescriptionComponent } from '../catalog-product-description/catalog-product-description.component';
import { CatalogProductGridComponent } from '../catalog-product-grid/catalog-product-grid.component';
import { TableDisplayModeComponent } from '../table-display-mode/table-display-mode.component';
import { TableSortModeComponent } from '../table-sort-mode/table-sort-mode.component';

interface ViewModel {
  form: FormArray;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-catalog-products',
  templateUrl: './catalog-products.component.html',
  styleUrls: ['./catalog-products.component.scss'],
  imports: [
    NgIf,
    TableV2Module,
    FormsModule,
    ReactiveFormsModule,
    TableDisplayModeComponent,
    TableSortModeComponent,
    MatButton,
    MatColumnDef,
    MatCellDef,
    MatCell,
    CatalogProductDescriptionComponent,
    MatFormField,
    MatInput,
    AgrFormModule,
    MatSuffix,
    MatIcon,
    FavoritesStarComponent,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    AgrNotificationEmptyModule,
    CatalogProductGridComponent,
    AsyncPipe,
    AgrI18nSharedModule,
    FormatAddProductToCartButtonTextPipe,
  ],
})
export class CatalogProductComponent implements OnInit {
  @ViewChild('openProductSearchButton') openProductSearchButton: MatButton;
  readonly hasAtLeastAdvisorRole$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);

  vm$: Observable<ViewModel>;
  isEmpty$: Observable<boolean>;

  dataSource: ProductsDataSource;
  columnsToDisplay: string[] = [];
  cartId: number;
  totalProductsFound: number | null;
  isEmptyText: string;
  tableDisplayMode: TableDisplayMode = TableDisplayMode.list;
  customer$ = this.store.select(UserSelectors.selectCustomer);
  selectedCatalogCategory$ = this.store.select(selectSelectedCatalogCategory);

  readonly search = new FormGroup({
    search: new FormControl(''),
  });

  readonly isDesktopDisplay$ = this.breakpointService.gtXs$;

  public isFavoritesEnabled = false;

  constructor(
    private readonly store: Store<RootState>,
    private readonly elasticApiService: ElasticApiService,
    private readonly removeSpecialCharactersPipe: RemoveSpecialCharactersPipe,
    private readonly localeService: LocaleService,
    @Inject(BreakpointServiceInjector) readonly breakpointService: BreakpointService,
    @Inject(EnvironmentInjector) readonly env: AgrEnvironment,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    this.dataSource = new ProductsDataSource(this.store, this.elasticApiService, this.hasAtLeastAdvisorRole$, env);
  }

  ngOnInit() {
    this.setSubscriptions();

    this.customer$
      .pipe(
        map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
        filter((salesOrganization) => !!salesOrganization),
        switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
      )
      .subscribe((featureFlags) => (this.isFavoritesEnabled = featureFlags.includes(ShopFeatureFlags.enableFavorites)));

    this.store.pipe(select(selectCatalogProductSearchString), take(1)).subscribe((searchString) => {
      this.search.patchValue({ search: searchString });
    });

    this.isEmptyText = this.localeService.translate('product.search.table.no-results.title');

    this.vm$ = this.dataSource.data$.pipe(
      map((products) => ({
        form: new FormArray(this.createForm(products)),
      })),
      shareReplay(1),
    );

    this.isEmpty$ = this.dataSource.data$.pipe(map((data) => data.length === 0));
  }

  openProductSearch() {
    this.store.dispatch(
      ProductActions.openSearchDialog({
        searchTerm: this.search.get('search').value,
        htmlButtonRef: this.openProductSearchButton._elementRef.nativeElement as HTMLButtonElement,
        source: CartLineSources.CATALOG_SEARCH,
      }),
    );
  }

  addToCart(selectedItem: CatalogItemVM, itemIndex?: number): void {
    const quantity$ = this.getProductQuantity(selectedItem, itemIndex);

    quantity$.pipe(take(1)).subscribe((quantity: number) => {
      this.store.dispatch(
        CartActions.addCartLineToCart({
          cartLine: {
            skuId: selectedItem.sku.skuId,
            quantity,
            hasProductSupplements: selectedItem.sku.hasProductSupplements,
            source: CartLineSources.CATALOG,
          },
          cartId: this.cartId,
        }),
      );
    });
  }

  openWizardDialog(selectedCatalogItem: CatalogItemVM, itemIndex?: number): void {
    if (!selectedCatalogItem.isUserAllowedToOrderItem) {
      return;
    }

    const quantity$ = this.getProductQuantity(selectedCatalogItem, itemIndex);

    quantity$.pipe(take(1)).subscribe((quantity: number) => {
      this.store.dispatch(
        ProductActions.openWizardDialog({
          skuCatalogItem: {
            ...selectedCatalogItem.sku,
            quantity,
          },
          source: CartLineSources.CATALOG,
        }),
      );
    });
  }

  setTableDisplayMode(tableDisplayMode: TableDisplayMode) {
    this.tableDisplayMode = tableDisplayMode;
  }

  onSortModeChange(sortMode: SortMode) {
    this.store.dispatch(
      setCatalogSortMode({
        catalogSortMode: sortMode,
      }),
    );
  }

  private setSubscriptions() {
    this.setStoreSubscriptions();
    this.setFormSubscriptions();
    this.setBreakPointServiceSubscriptions();
  }

  private setStoreSubscriptions() {
    this.store.select(CartSelectors.selectCurrentSalesOrderCart).subscribe((cart) => (this.cartId = cart?.id));

    this.store.select(selectcatalogTotalNumberOfProductsFound).subscribe((total) => (this.totalProductsFound = total));
  }

  private setFormSubscriptions() {
    this.search
      .get('search')
      .valueChanges.pipe(debounceTime(300))
      .subscribe((value: string) =>
        this.store.dispatch(
          setCatalogProductSearchString({
            catalogProductSearchString: this.removeSpecialCharactersPipe.transform(value).trim(),
          }),
        ),
      );
  }

  private setBreakPointServiceSubscriptions() {
    this.breakpointService.ltSm$.subscribe((ltSm) => {
      if (ltSm) {
        this.columnsToDisplay = ['singleMobileColumn'];
      } else {
        this.columnsToDisplay = ['description', 'quantity', 'favoriteStar', 'addToCart'];
      }
    });
  }

  private createForm(data: readonly CatalogItemVM[]) {
    return data.map(
      (item) =>
        new FormGroup({
          id: new FormControl(item.sku.skuId),
          quantity: new FormControl(
            item.sku.orderingInformation.minimumOrderUnitQuantity,
            [
              Validators.required,
              item.sku.orderingInformation?.increaseOrderUnitQuantity
                ? validateStep(item.sku.orderingInformation?.increaseOrderUnitQuantity, item.sku.orderingInformation?.maximumOrderUnitQuantity)
                : undefined,
              item.sku.orderingInformation?.minimumOrderUnitQuantity ? Validators.min(item.sku.orderingInformation?.minimumOrderUnitQuantity) : undefined,
              item.sku.orderingInformation?.maximumOrderUnitQuantity ? Validators.max(item.sku.orderingInformation?.maximumOrderUnitQuantity) : undefined,
            ].filter((it) => !!it),
          ),
        }),
    );
  }

  private getProductQuantity(item: CatalogItemVM, index?: number) {
    return index >= 0 ? this.vm$.pipe(map((vm) => vm.form.at(index).get('quantity').value)) : of(item.sku.orderingInformation.minimumOrderUnitQuantity);
  }
}
